import { useMemo } from 'react'
import { Space, Table, Tag, Typography } from 'antd'
import { CheckCircleOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { BillingUsageItem } from '../types/billing.types'
import type { ColumnsType } from 'antd/es/table'
import { compareWorkspaceName, resolveWorkspaceName } from '../utils/workspace-name'
import './BillingUsageTable.css'

const { Text } = Typography

interface BillingUsageTableProps {
  items: BillingUsageItem[]
  loading: boolean
}

// One row per (tenant, period); each module becomes its own column.
interface PivotRow {
  tenantCode: string
  tenantName: string | null
  workspaceCode: string | null
  period: string
  calculatedDate: string
  byModule: Record<string, BillingUsageItem>
}

// Canonical module column order; any module not listed is appended (sorted) after these.
const MODULE_ORDER = ['people', 'payroll', 'claim', 'leave', 'attendance', 'performance']

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

export function BillingUsageTable({ items, loading }: BillingUsageTableProps) {
  const { rows, modules } = useMemo(() => {
    const present = new Set(items.map(i => i.module))
    const orderedModules = [
      ...MODULE_ORDER.filter(m => present.has(m)),
      ...[...present].filter(m => !MODULE_ORDER.includes(m)).sort()
    ]

    const byKey = new Map<string, PivotRow>()
    for (const item of items) {
      const key = `${item.tenantCode}::${item.period}`
      let row = byKey.get(key)
      if (!row) {
        row = {
          tenantCode: item.tenantCode,
          tenantName: item.tenantName,
          workspaceCode: item.workspaceCode,
          period: item.period,
          calculatedDate: item.calculatedDate,
          byModule: {}
        }
        byKey.set(key, row)
      }
      row.byModule[item.module] = item
      if (new Date(item.calculatedDate).getTime() > new Date(row.calculatedDate).getTime()) {
        row.calculatedDate = item.calculatedDate
      }
    }

    return { rows: [...byKey.values()], modules: orderedModules }
  }, [items])

  const moduleColumns: ColumnsType<PivotRow> = modules.map(module => ({
    title: capitalize(module),
    key: module,
    children: [
      {
        title: 'Usage',
        key: `${module}_usage`,
        width: 90,
        align: 'right' as const,
        sorter: (a: PivotRow, b: PivotRow) =>
          (a.byModule[module]?.billableHeadcount ?? -1) - (b.byModule[module]?.billableHeadcount ?? -1),
        render: (_: unknown, row: PivotRow) => {
          const cell = row.byModule[module]
          if (!cell) return <Text type="secondary">—</Text>
          const isFinalised = cell.status === 'Finalised'
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
              <Text strong>{cell.billableHeadcount}</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                / {cell.purchasedLicenses}
              </Text>
              {isFinalised && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 11 }} />}
            </span>
          )
        }
      },
      {
        title: 'Excess',
        key: `${module}_excess`,
        width: 60,
        align: 'right' as const,
        sorter: (a: PivotRow, b: PivotRow) =>
          (a.byModule[module]?.excessUsage ?? -1) - (b.byModule[module]?.excessUsage ?? -1),
        render: (_: unknown, row: PivotRow) => {
          const cell = row.byModule[module]
          if (!cell || cell.excessUsage === 0) return null
          return (
            <Tag color="red" style={{ fontSize: 10, margin: 0, lineHeight: '16px', padding: '0 4px' }}>
              +{cell.excessUsage}
            </Tag>
          )
        }
      }
    ]
  }))

  const columns: ColumnsType<PivotRow> = [
    {
      title: 'Workspace',
      key: 'tenant',
      width: 300,
      fixed: 'left',
      sorter: compareWorkspaceName,
      render: (_, row) => (
        <Space size={8} wrap={false} style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
          <Text ellipsis style={{ maxWidth: '150px' }}>
            {resolveWorkspaceName(row)}
          </Text>
          <span>
            <Tag color="geekblue">{row.tenantCode.toLowerCase()}</Tag>
            {row.workspaceCode && <Tag color="default">{row.workspaceCode.toLowerCase()}</Tag>}
          </span>
        </Space>
      )
    },
    ...moduleColumns,
    {
      title: 'Calculated',
      dataIndex: 'calculatedDate',
      key: 'calculatedDate',
      width: 160,
      sorter: (a, b) => new Date(a.calculatedDate).getTime() - new Date(b.calculatedDate).getTime(),
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {dayjs(date).format('DD MMM YYYY, HH:mm')}
        </Text>
      )
    }
  ]

  return (
    <div className="billing-table-wrapper" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Table
        size="small"
        columns={columns}
        dataSource={rows}
        rowKey={row => `${row.tenantCode}-${row.period}`}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={false}
        style={{ flex: 1 }}
      />
    </div>
  )
}
